<template>
    <div>
        <rounded-container variant="dark">
            <template v-slot:title>
                <slot name="title">Options</slot>
            </template>

            <b-row class="p-3">
                <b-col>
                    <b-form-select v-model="selected" :options="selectOptions" size="sm"></b-form-select>
                </b-col>
                <b-col>
                    <b-form-select v-model="selectedSort" :options="sortOptions" size="sm"></b-form-select>
                </b-col>
            </b-row>
        </rounded-container>

        <rounded-container variant="dark">
            <b-table
                hover
                dark
                striped
                borderless
                thead-class="hidden_header"
                class="mb-0"

                :fields="columns"
                :items="rowsIncludingTotal"

            >
                <template #cell(symbol)="data">
                    <span v-if="data.item.asset === 'Total'" class="font-weight-bold">Total</span>
                    <asset-symbol v-else :asset="data.item.asset" />
                </template>

                <template #cell(percentage)="data">
                    <span v-if="data.item.asset !== 'Total'">
                        <span v-if="data.item.totalValue / totalValue * 100 <= 1" class="text-secondary">
                            {{ (data.item.totalValue / totalValue * 100).toFixed(2) }}%
                        </span>
                        <span v-else>
                            {{ (data.item.totalValue / totalValue * 100).toFixed(1) }}%
                        </span>
                    </span>
                </template>

                <template #cell(value)="data">
                    <span v-if="data.item.asset === 'Total'" class="font-weight-bold">${{ formatHoldings(data.item.totalValue) }}</span>
                    <span v-else>${{ formatHoldings(data.item.totalValue) }}</span>
                </template>

                <template #cell(shares)="data">
                    <span v-if="data.item.asset === 'Total'"></span>
                    <span v-else>{{ formatHoldings(data.item.shares) }}</span>
                </template>
            </b-table>
        </rounded-container>

        <portfolio-historical-value
            v-if="selectedMarket"
            :market="selectedMarket"
        />

        <rounded-container variant="dark" v-if="hasSplitData">
            <b-row class="my-4 justify-content-center">
                <b-col cols="6">
                    <pie-chart
                        :chart-data="splitChartData"
                        :styles="{ position: 'relative' }"
                    />
                </b-col>
            </b-row>

            <b-table
                dark
                striped
                borderless
                thead-class="hidden_header"
                class="mb-0"
                :fields="splitFields"
                :items="splitRows"
            >
                <template #cell(conviction)="data">
                    <span
                        class="conviction-label"
                        :style="{ backgroundColor: data.item.color }"
                    >{{ data.item.conviction }}</span>
                </template>

                <template #cell(percentage)="data">
                    {{ data.item.percentage }}%
                </template>

                <template #cell(value)="data">
                    ${{ formatHoldings(data.item.value) }}
                </template>

                <template #cell(moneyIn)="data">
                    <span class="text-secondary">${{ formatHoldings(data.item.moneyIn) }}</span>
                </template>
            </b-table>
        </rounded-container>

        
    </div>
</template>

<script>

// import axios from 'axios';
import { mapState } from 'vuex';
import formatter from '@/mixins/formatter'

import RoundedContainer from '@/components/style/RoundedContainer.vue'
import AssetSymbol from '@/components/style/AssetSymbol.vue';
import PieChart from '@/components/charts/PieChart.vue';
import PortfolioHistoricalValue from '@/components/portfolio/PortfolioHistoricalValue.vue';

const CONVICTION_CONFIG = {
    '100k':   { label: '100K',    color: '#5B8E7D' },
    'high':   { label: 'High',    color: '#8CB369' },
    'null':   { label: 'Regular', color: '#F4E285' },
    'low':    { label: 'Low',     color: '#F4A259' },
    'snoozed':{ label: 'Snoozed', color: '#922D50' },
};

export default {
    name: 'Portfolio',
    components: {
        RoundedContainer,
        AssetSymbol,
        PieChart,
        PortfolioHistoricalValue
    },
    mixins: [formatter],
    data() {
        return {
            selected: null,
            selectedSort: 'valueDesc',
            sortOptions: [
                { value: 'valueDesc', text: 'Value (desc)' },
                { value: 'symbolAsc', text: 'Symbol (asc)' }
            ],
            columns: [
                { key: 'symbol', label: "" },
                { key: 'percentage', label: "% of"},
                { key: 'value', label: "Value" },
                { key: 'shares', label: "Holdings" },
            ],
        }
    },
    mounted() {
        for (const eachOption of this.selectOptions) {
            if (eachOption.value === 'market:stock') {
                this.selected = 'market:stock';
                break;
            }
        }

    },
    computed: {
        ...mapState({
            assets: state => state.assets.assetList,
        }),
        activeAssets() {
            if (!this.assets) {
                return [];
            }

            return this.assets.filter(asset => asset && asset.status !== 'disabled');
        },
        assetsByPublicId() {
            if (!this.activeAssets) {
                return {};
            } 

            return this.activeAssets.reduce((acc, asset) => {
                acc[asset.public_id] = asset;
                return acc;
            }, {});
        },
        balancesByBucket() {
            if (!this.activeAssets) {
                return {};
            } 

            return this.activeAssets.reduce((acc, asset) => {
                for (const balance of asset.balances) {
                    if (!acc[balance.bucket.public_id]) {
                        acc[balance.bucket.public_id] = [];
                    }

                    acc[balance.bucket.public_id].push({
                        ...balance,
                        asset_public_id: asset.public_id,
                    });
                }

                return acc;
            }, {});
        },
        assetPublicIdsByMarket() {
            if (!this.activeAssets) {
                return {};
            } 

            return this.activeAssets.reduce((acc, asset) => {
                if (!asset.market) {
                    return acc;
                }

                if (!acc[asset.market]) {
                    acc[asset.market] = [];
                }

                acc[asset.market].push(asset.public_id);
                return acc;
            }, {});
        },
        selectedMarket() {
            if (!this.selected) return null;
            const [type, id] = this.selected.split(':');
            return type === 'market' ? id : null;
        },
        selectOptions() {
            const options = [];
            for (const eachMarket of Object.keys(this.assetPublicIdsByMarket)) {
                options.push({
                    value: 'market:' + eachMarket,
                    text: `${eachMarket} market`,
                });
            }

            for (const eachBucketPublicId of Object.keys(this.balancesByBucket)) {
                options.push({
                    value: 'bucket:' + eachBucketPublicId,
                    text: this.balancesByBucket[eachBucketPublicId][0].bucket.name,
                });
            }

            
            return options;
        },
        rows() {
            let rows = [];

            if (!this.selected) {
                return rows;
            }

            const [type, id] = this.selected.split(':');
            const inMarketMode = type === 'market';

            const buildMoneyIn = (asset) => {
                if (!inMarketMode) return 0;
                const cached = asset.cached_stats || {};
                const openValue = cached.open_positions_value || 0;
                const bonus = cached.sum_bonus_exit_value || 0;
                return Math.max(0, openValue - bonus);
            };

            if (type === 'market') {
                const assetPublicIds = this.assetPublicIdsByMarket[id];
                for (const eachAssetPublicId of assetPublicIds) {
                    const asset = this.assetsByPublicId[eachAssetPublicId];
                    const row = {
                        asset: asset,
                        shares: asset.balances.reduce((acc, balance) => {
                            return acc + balance.shares;
                        }, 0)
                    }
                    row.totalValue = row.asset.price * row.shares;
                    row.moneyIn = buildMoneyIn(asset);

                    if (row.shares > 0) {
                        rows.push(row);
                    }
                }
            } else if (type === 'bucket') {
                for (const eachBalance of this.balancesByBucket[id]) {
                    const row = {
                        asset: this.assetsByPublicId[eachBalance.asset_public_id],
                        shares: eachBalance.shares,
                    }
                    row.totalValue = row.asset.price * row.shares;
                    row.moneyIn = buildMoneyIn(row.asset);

                    if (row.shares > 0) {
                        rows.push(row);
                    }
                }
            }

            if (this.selectedSort === 'symbolAsc') {
                rows = rows.sort((a, b) => a.asset.symbol.localeCompare(b.asset.symbol));
            } else {
                rows = rows.sort((a, b) => (a.totalValue < b.totalValue) ? 1 : -1);
            }

            return [
                ...rows
            ]
        },
        totalValue() {
            return this.rows.reduce((acc, row) => acc + row.totalValue, 0);
        },
        rowsIncludingTotal() {
            return [
                {
                    asset: 'Total',
                    shares: 0,
                    totalValue: this.totalValue
                },
                ...this.rows
            ];
        },
        splitBuckets() {
            const buckets = {};
            for (const key of Object.keys(CONVICTION_CONFIG)) {
                buckets[key] = { totalValue: 0, moneyIn: 0 };
            }

            for (const row of this.rows) {
                if (row.totalValue <= 0) continue;
                const rawKey = row.asset.conviction === null ? 'null' : row.asset.conviction;
                const key = buckets[rawKey] !== undefined ? rawKey : 'null';
                buckets[key].totalValue += row.totalValue;
                buckets[key].moneyIn += row.moneyIn;
            }

            return buckets;
        },
        splitChartData() {
            const labels = [];
            const data = [];
            const colors = [];

            for (const [key, bucket] of Object.entries(this.splitBuckets)) {
                if (bucket.totalValue <= 0) continue;
                const cfg = CONVICTION_CONFIG[key];
                labels.push(cfg.label);
                data.push(Math.round(bucket.totalValue));
                colors.push(cfg.color);
            }

            return {
                labels,
                datasets: [{
                    data,
                    backgroundColor: colors,
                }],
            };
        },
        hasSplitData() {
            return this.splitChartData.datasets[0].data.length > 0;
        },
        splitFields() {
            const fields = ['conviction', 'percentage', 'value'];
            if (this.selectedMarket) fields.push('moneyIn');
            return fields;
        },
        splitRows() {
            const total = Object.values(this.splitBuckets).reduce((acc, b) => acc + b.totalValue, 0);
            const rows = [];

            for (const [key, bucket] of Object.entries(this.splitBuckets)) {
                if (bucket.totalValue <= 0) continue;
                const cfg = CONVICTION_CONFIG[key];
                rows.push({
                    conviction: cfg.label,
                    color: cfg.color,
                    percentage: ((bucket.totalValue / total) * 100).toFixed(1),
                    value: Math.round(bucket.totalValue),
                    moneyIn: Math.round(bucket.moneyIn),
                });
            }

            return rows;
        },
    },
}
</script>

<style>
.hidden_header {
    display: none;
}
.conviction-label {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 4px;
    font-weight: 600;
    color: #fff;
}
</style>